import { useId } from 'react';
import PhoneInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
import countryLabels from 'react-phone-number-input/locale/fr.json';
import 'react-phone-number-input/style.css';
import { WarningCircle } from 'assets/icons';
import BillingContactNotificationChoices from 'components/billingContacts/BillingContactNotificationChoices';
import StyledInput from 'components/styledInput/StyledInput';
import UseContext from 'hooks/useContext';

/**
 * The fields of a billing contact without an account (mockups 8.5 and 8.6):
 * email, first name, last name, optional number, and the two boxes. Controlled
 * and without any request: `form` comes from billingContactForm.js, `errors`
 * holds texts ready to show ({ email, firstName, lastName, phone,
 * notifications }), and `onChange(field, value)` reports each edit.
 */
const BillingContactExternalForm = ({
  form,
  errors = {},
  onChange,
  isDisabled = false,
}) => {
  const { language } = UseContext();
  const text = (key) =>
    language.getValue(`companyBillingContacts.external.${key}`, '');
  const phoneFieldId = useId();

  const fromEvent = (field) => (event) => onChange(field, event.target.value);

  return (
    <div className={'flex flex-col gap-4 text-sm'}>
      <StyledInput
        label={text('email')}
        type={'email'}
        value={form.email}
        onChange={fromEvent('email')}
        error={errors.email}
        isDisabled={isDisabled}
      />
      <div className={'grid grid-cols-2 gap-3'}>
        <StyledInput
          label={text('firstName')}
          type={'text'}
          value={form.firstName}
          onChange={fromEvent('firstName')}
          error={errors.firstName}
          isDisabled={isDisabled}
        />
        <StyledInput
          label={text('lastName')}
          type={'text'}
          value={form.lastName}
          onChange={fromEvent('lastName')}
          error={errors.lastName}
          isDisabled={isDisabled}
        />
      </div>
      <div className={'flex flex-col gap-1 min-w-0 w-full'}>
        <label htmlFor={phoneFieldId} className={'ft-sm text-gray-500'}>
          {text('phone')}
        </label>
        <PhoneInput
          // Same settings as the invitation modal (ModalAddCompanyMember): a
          // pasted "+590…" is kept, and a stored number shows in national
          // format with the flag of its overseas department.
          defaultCountry={'FR'}
          initialValueFormat={'national'}
          flags={flags}
          labels={countryLabels}
          value={form.phone || undefined}
          onChange={(value) => onChange('phone', value ?? '')}
          disabled={isDisabled}
          className={
            'flex items-center gap-2 h-10 border rounded-md px-2 focus-within:border-yellow-500'
          }
          style={{
            '--PhoneInputCountrySelectArrow-color--focus': '#ff9600',
            '--PhoneInputCountryFlag-borderColor--focus': '#ff9600',
          }}
          numberInputProps={{
            id: phoneFieldId,
            className:
              'flex-1 min-w-0 h-full text-sm bg-transparent border-0 focus:outline-none',
          }}
        />
        {errors.phone ? (
          <div className={'text-red-500 flex items-center gap-1 text-xs'}>
            <WarningCircle wh={16} color={'#EF4444'} />
            {errors.phone}
          </div>
        ) : null}
      </div>
      <BillingContactNotificationChoices
        invoices={form.invoices}
        reminders={form.reminders}
        onChange={onChange}
        isDisabled={isDisabled}
        error={errors.notifications}
      />
    </div>
  );
};

export default BillingContactExternalForm;
